import { formatLocalDate } from '../utils/localDateTime';
import { showAppAlert } from '../common/appDialog';
import React, { useState, useEffect } from 'react';
import { 
    DocumentChartBarIcon, 
    QueueListIcon, 
    ChartBarSquareIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowPathIcon,
    EyeIcon,
    ArrowDownTrayIcon,
    ShieldCheckIcon,
    SparklesIcon,
    ArchiveBoxIcon
} from '@heroicons/react/24/outline';
import type { CompositeReportRequest, Page, NavParams, ReportSection } from '@types';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebaseConfig';
import ReportBuilder from './ReportBuilder';
import { ICONS } from './constants';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

interface ReportHubProps {
    isLoading: boolean;
    onSubmit: (event: React.FormEvent, reportData: CompositeReportRequest) => void;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
    initialTab?: TabType;
    currentOrganization?: any;
    currentUser?: any;
}

type TabType = 'generation' | 'repository' | 'analytics';

const ReportHub: React.FC<ReportHubProps> = ({ isLoading, onSubmit, onNavigate, initialTab, currentOrganization, currentUser }) => {
    const [activeTab, setActiveTab] = useState<TabType>(initialTab || 'generation');
    const [reports, setReports] = useState<any[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    // ✅ FIX: Sections state lifted here so it survives tab switches (prevents generate button reset bug)
    const [reportSections, setReportSections] = useState<ReportSection[]>([]);
    
    // Operational Filters
    const [selectedTeam, setSelectedTeam] = useState('all');
    const [selectedDept, setSelectedDept] = useState('all');
    const [selectedUser, setSelectedUser] = useState('all');
    const [dateRange, setDateRange] = useState('30d');

    useEffect(() => {
        if (activeTab === 'repository' || activeTab === 'analytics') {
            fetchReports();
        }
    }, [activeTab]);

    const fetchReports = async () => {
        setIsFetching(true);
        try {
            const executiveReportsRef = collection(db, 'generated_executive_reports');
            const operationalBriefsRef = collection(db, 'generated_operational_briefs');
            const savedIntelRef = collection(db, 'saved_intel');
            
            let q1, q2, q3;
            
            if (currentOrganization?.id) {
                q1 = query(
                    executiveReportsRef, 
                    where("organizationId", "==", currentOrganization.id),
                    orderBy('timestamp', 'desc')
                );
                q2 = query(
                    operationalBriefsRef, 
                    where("organizationId", "==", currentOrganization.id),
                    orderBy('timestamp', 'desc')
                );
            } else {
                q1 = query(executiveReportsRef, orderBy('timestamp', 'desc'), limit(30));
                q2 = query(operationalBriefsRef, orderBy('timestamp', 'desc'), limit(30));
            }

            // Always fetch user's personal findings library
            if (currentUser?.uid) {
                q3 = query(
                    savedIntelRef,
                    where("userId", "==", currentUser.uid),
                    orderBy('savedAt', 'desc'),
                    limit(50)
                );
            }

            const [snap1, snap2, snap3] = await Promise.all([
                getDocs(q1), 
                getDocs(q2),
                q3 ? getDocs(q3) : Promise.resolve({ docs: [] })
            ]) as any;
            
            const fetchedReports = [
                ...snap1.docs.map((doc: any) => ({ id: doc.id, reportType: 'Executive', ...doc.data() })),
                ...snap2.docs.map((doc: any) => ({ id: doc.id, reportType: 'Operational', ...doc.data() })),
                ...snap3.docs.map((doc: any) => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        reportType: 'Finding',
                        reportTitle: data.title || data.country || data.groupName || 'Intelligence Finding',
                        timestamp: data.savedAt,
                        ...data,
                        config: {
                            ...data.config,
                            pillars: {
                                intelligence: true,
                                ...data.config?.pillars
                            }
                        }
                    };
                })
            ].sort((a: any, b: any) => {
                const dateA = new Date(a.timestamp || a.dateGenerated || 0).getTime();
                const dateB = new Date(b.timestamp || b.dateGenerated || 0).getTime();
                return dateB - dateA;
            });

            setReports(fetchedReports);
        } catch (error) {
            console.error("Error fetching reports:", error);
        } finally {
            setIsFetching(false);
        }
    };

    const filteredReports = reports.filter(report => {
        const matchesSearch = report.organizationName?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                             report.config?.timeframe?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesType = filterType === 'all' || 
                           (filterType === 'intelligence' && report.config?.pillars?.intelligence) ||
                           (filterType === 'security' && report.config?.pillars?.security) ||
                           (filterType === 'resilience' && report.config?.pillars?.resilience);
        
        // Advanced Operational Filters
        const matchesTeam = selectedTeam === 'all' || 
                           report.config?.filters?.team === selectedTeam || 
                           report.metadata?.team === selectedTeam;
        const matchesDept = selectedDept === 'all' || 
                           report.config?.filters?.dept === selectedDept || 
                           report.metadata?.department === selectedDept;
        const matchesUser = selectedUser === 'all' || 
                           report.metadata?.generatedBy === selectedUser;
        
        return matchesSearch && matchesType && matchesTeam && matchesDept && matchesUser;
    });

    const [analyticsData, setAnalyticsData] = useState<any>({
        riskTrend: [],
        moduleCompletion: [],
        saturation: 0,
        reportStats: [],
        suiteStats: [],
        velocity: 0,
        findingsMeta: {
            summary: "Analyzing historical intelligence library...",
            topThemes: [],
            threatActors: []
        }
    });

    const [isRefreshingMeta, setIsRefreshingMeta] = useState(false);

    const getRiskColor = (score: number) => {
        if (score >= 70) return '#f43f5e'; // Critical (Red)
        if (score >= 40) return '#fbbf24'; // Elevated (Amber)
        return '#10b981'; // Low (Green)
    };

    const getRiskLabel = (score: number) => {
        if (score >= 70) return 'CRITICAL';
        if (score >= 40) return 'ELEVATED';
        return 'STABLE';
    };

    const handleManualRefresh = async () => {
        if (!functions) return;
        setIsFetching(true);
        try {
            const triggerFn = httpsCallable(functions, 'triggerGlobalBatchAnalysis');
            await triggerFn();
            void showAppAlert("Global intelligence refresh triggered. 197-country batch is now staging in us-central1.");
            await getAnalyticsData();
        } catch (err) {
            console.error("Refresh failed:", err);
            void showAppAlert("Failed to trigger global refresh. Check permissions.");
        } finally {
            setIsFetching(false);
        }
    };

    const getAnalyticsData = async () => {
        setIsFetching(true);
        try {
            // 1. Weekly Global Risk Trends (6 months)
            const historyRef = collection(db, 'global_risk_history');
            const historySnap = await getDocs(query(historyRef, orderBy('timestamp', 'desc'), limit(25)));
            
            const trend = historySnap.docs.map(doc => {
                const d = doc.data();
                const score = d.avgRiskScore || 0;
                return {
                    label: d.weekLabel || 'W',
                    value: score,
                    color: getRiskColor(score)
                };
            }).reverse();

            // 7-Day Velocity & Drilling
            const latest = trend[trend.length - 1]?.value || 0;
            const previous = trend[trend.length - 2]?.value || latest;
            const velocity = latest - previous;

            // Fetch 7-Day Risk Events (Drilling)
            const eventsRef = collection(db, 'risk_events');
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            const eventsSnap = await getDocs(query(eventsRef, where('processedAt', '>=', sevenDaysAgo), limit(20)));
            const drillEvents = eventsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // 2. SRM Module Saturation
            const srmProjectsRef = collection(db, 'srm_projects');
            const projectsSnap = await getDocs(srmProjectsRef);
            
            let totalModulesCompleted = 0;
            const moduleStats: Record<string, number> = {};
            const totalProjects = projectsSnap.size || 1;
            const totalPossibleModules = totalProjects * 22;
            
            projectsSnap.forEach(doc => {
                const data = doc.data();
                Object.entries(data.modules || {}).forEach(([m, config]: [string, any]) => {
                    if (config?.status === 'completed') {
                        totalModulesCompleted++;
                        moduleStats[m] = (moduleStats[m] || 0) + 1;
                    }
                });
            });

            const saturation = totalPossibleModules > 0 ? Math.round((totalModulesCompleted / totalPossibleModules) * 100) : 0;
            const moduleCompletion = Object.entries(moduleStats).map(([name, count]) => ({
                name: name.replace(/_/g, ' ').toUpperCase(),
                value: Math.round((count / totalProjects) * 100)
            })).sort((a,b) => b.value - a.value).slice(0, 8);

            // 3. Suite Statistics
            const suitesUsed: Record<string, number> = {};
            reports.forEach(r => {
                const suite = r.config?.activeSuite || 'General Intelligence';
                suitesUsed[suite] = (suitesUsed[suite] || 0) + 1;
            });

            const suiteStats = Object.entries(suitesUsed).map(([name, count]) => ({
                name,
                value: count
            })).sort((a, b) => b.value - a.value);

            setAnalyticsData({
                riskTrend: trend.length > 0 ? trend : Array.from({ length: 12 }).map((_, i) => ({ label: `W${i+1}`, value: 40 + Math.random() * 10, color: '#6366f1' })),
                moduleCompletion,
                saturation,
                suiteStats,
                velocity,
                drillEvents,
                reportStats: [
                    { name: 'Executive', value: reports.filter(r => r.reportType === 'Executive').length },
                    { name: 'Operational', value: reports.filter(r => r.reportType === 'Operational').length },
                    { name: 'Findings', value: reports.filter(r => r.reportType === 'Finding').length }
                ],
                findingsMeta: analyticsData.findingsMeta
            });

            if (reports.length > 0) {
                generateFindingsMetaAnalysis(reports);
            }
        } catch (error) {
            console.error("Analytics fetch failed:", error);
        } finally {
            setIsFetching(false);
        }
    };

    const generateFindingsMetaAnalysis = async (reports: any[]) => {
        if (!functions || isRefreshingMeta) return;
        setIsRefreshingMeta(true);
        try {
            const reportSummaries = reports.slice(0, 15).map(r => ({
                title: r.reportTitle || r.organizationName || r.countryName || 'Untitled Report',
                summary: r.executiveSummary || r.summary || r.analysis?.summary || 'No summary available',
                type: r.reportType || 'General',
                riskScore: r.analysis?.risk_score || r.riskScore || 50
            }));

            const generateContentFn = httpsCallable(functions, 'generateContent');
            const response: any = await generateContentFn({
                workflow: 'integrated_intelligence',
                contents: [{
                    role: 'user',
                    parts: [{
                        text: `Act as a Lead Intelligence Analyst. Synthesize the following 10 intelligence reports into a strategic meta-analysis.
                        
                        REPORTS:
                        ${JSON.stringify(reportSummaries)}

                        OUTPUT JSON FORMAT:
                        {
                            "summary": "A 3-4 sentence high-level strategic overview of recurring threats and operational status.",
                            "topThemes": ["Theme 1", "Theme 2", "Theme 3"],
                            "threatActors": ["Actor/Catalyst 1", "Actor/Catalyst 2"]
                        }`
                    }]
                }]
            });

            const result = JSON.parse(response.data.text);
            setAnalyticsData((prev: any) => ({
                ...prev,
                findingsMeta: result
            }));
        } catch (err) {
            console.error("Meta-analysis failed:", err);
        } finally {
            setIsRefreshingMeta(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'analytics') {
            getAnalyticsData();
        }
    }, [activeTab, reports]);

    const renderHeader = () => (
        <div className="bg-slate-900 -mx-6 -mt-6 p-8 mb-8 border-b border-white/10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-600/10 to-transparent pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                    <div className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-black rounded tracking-widest uppercase shadow-lg shadow-blue-600/20">
                        Strategic Assets
                    </div>
                    <div className="h-4 w-px bg-white/20" />
                    <div className="text-slate-400 font-bold text-[10px] tracking-[0.3em] uppercase">
                        IntelOptics Ecosystem / Reporting
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
                    <div>
                        <h2 className="text-4xl font-black text-white tracking-tighter leading-none uppercase mb-2">
                            Strategic Reporting <span className="text-blue-500">Hub</span>
                        </h2>
                        <p className="text-slate-400 font-medium max-w-2xl text-sm leading-relaxed">
                            A centralized intelligence repository and analytical workspace. Generate organization-wide 
                            usage insights, historical performance metrics, and composite multi-pillar strategic reports.
                        </p>
                    </div>

                    <div className="flex items-center gap-1 bg-white/5 p-1.5 rounded-xl border border-white/10 backdrop-blur-md">
                        <button 
                            onClick={() => setActiveTab('generation')}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'generation' ? 'bg-white text-slate-900 shadow-xl' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                        >
                            <DocumentChartBarIcon className="h-4 w-4" />
                            Generation
                        </button>
                        <button 
                            onClick={() => setActiveTab('repository')}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'repository' ? 'bg-white text-slate-900 shadow-xl' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                        >
                            <QueueListIcon className="h-4 w-4" />
                            Repository
                        </button>
                        <button 
                            onClick={() => setActiveTab('analytics')}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'analytics' ? 'bg-white text-slate-900 shadow-xl' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                        >
                            <ChartBarSquareIcon className="h-4 w-4" />
                            Analytics
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {renderHeader()}

            <div className="animate-in fade-in duration-1000 delay-300">
                {activeTab === 'generation' && (
                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                        <div className="xl:col-span-3">
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
                                <div className="bg-slate-50 border-b border-slate-200 px-8 py-6 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-tighter">Strategic Report Constructor</h3>
                                        <p className="text-[11px] text-slate-500 font-bold uppercase mt-0.5 tracking-tight">Mission Matrix / Configuration v4</p>
                                    </div>
                                    <div className="p-2 bg-indigo-50 rounded-lg">
                                        {ICONS.folder}
                                    </div>
                                </div>
                                <div className="p-8">
                                    <ReportBuilder
                                        isLoading={isLoading}
                                        onSubmit={onSubmit}
                                        sections={reportSections}
                                        onSectionsChange={setReportSections}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 shadow-2xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full -mr-16 -mt-16 blur-3xl" />
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Quick Stats</h4>
                                <div className="space-y-4 relative z-10">
                                    <div className="flex justify-between items-end border-b border-white/10 pb-3">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase">Total Generated</span>
                                        <span className="text-2xl font-black text-white">{reports.length}</span>
                                    </div>
                                    <div className="flex justify-between items-end border-b border-white/10 pb-3">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase">Avg Confidence</span>
                                        <span className="text-2xl font-black text-emerald-400">94.2%</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                                <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                    Reporting Guidelines
                                </h4>
                                <ul className="space-y-3 text-[11px] text-slate-600 font-medium">
                                    <li className="flex gap-2">
                                        <span className="text-blue-500 font-black">01</span>
                                        Select at least two pillars for a composite analysis score.
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-blue-500 font-black">02</span>
                                        Timeframe filters affect underlying intelligence look-back.
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-blue-500 font-black">03</span>
                                        Reports are encrypted and stored in the secure repository.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'repository' && (
                    <div className="space-y-6">
                        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                            <div className="relative flex-grow max-w-md w-full">
                                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <input 
                                    type="text" 
                                    placeholder="Search by ID or Organization..." 
                                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 outline-none transition-all"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="flex items-center gap-3 w-full md:w-auto">
                                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-200">
                                    <FunnelIcon className="h-4 w-4 text-slate-500" />
                                    <select 
                                        className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer"
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                    >
                                        <option value="all">ALL PILLARS</option>
                                        <option value="intelligence">INTELLIGENCE</option>
                                        <option value="security">SECURITY</option>
                                        <option value="resilience">RESILIENCE</option>
                                    </select>
                                </div>
                                <button 
                                    onClick={fetchReports}
                                    className="p-3 bg-slate-900 text-white rounded-xl hover:bg-black transition-all shadow-lg shadow-slate-900/20"
                                >
                                    <ArrowPathIcon className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Archive / ID</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Timestamp</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Pillars</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Scope</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {isFetching ? (
                                        Array(3).fill(0).map((_, i) => (
                                            <tr key={i} className="animate-pulse">
                                                <td className="px-8 py-6"><div className="h-4 bg-slate-100 rounded w-48"></div></td>
                                                <td className="px-8 py-6"><div className="h-4 bg-slate-100 rounded w-24"></div></td>
                                                <td colSpan={3} />
                                            </tr>
                                        ))
                                    ) : filteredReports.length > 0 ? (
                                        filteredReports.map((report) => (
                                            <tr key={report.id} className="hover:bg-slate-50 transition-colors group">
                                                <td className="px-8 py-6">
                                                    <div className="font-black text-slate-900 text-sm uppercase tracking-tighter">
                                                        {report.reportTitle || report.organizationName || 'Strategic Brief'}
                                                    </div>
                                                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">ID: {report.id}</div>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <div className="text-sm font-black text-slate-900">
                                                        {formatLocalDate(report.timestamp || report.dateGenerated)}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {report.config?.pillars?.intelligence && <span className="text-[9px] font-black bg-blue-50 text-blue-700 px-2 py-0.5 rounded shadow-sm">INTEL</span>}
                                                        {report.config?.pillars?.security && <span className="text-[9px] font-black bg-slate-100 text-slate-700 px-2 py-0.5 rounded shadow-sm">SEC</span>}
                                                        {report.reportType === 'Finding' && <span className="text-[9px] font-black bg-amber-50 text-amber-700 px-2 py-0.5 rounded shadow-sm">FINDING</span>}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6 text-sm font-bold text-slate-500 uppercase tracking-tighter">
                                                    {report.config?.timeframe || 'DYNAMIC'}
                                                </td>
                                                <td className="px-8 py-6 text-right">
                                                    <button 
                                                        onClick={() => onNavigate?.({ page: 'executiveReport', params: { reportData: report.reportData, config: report.config } })}
                                                        className="px-4 py-2 bg-white border border-slate-200 text-[10px] font-black uppercase text-slate-900 rounded-lg hover:bg-slate-900 hover:text-white transition-all shadow-sm"
                                                    >
                                                        Review
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="px-8 py-20 text-center text-slate-400 font-bold uppercase text-xs tracking-widest">
                                                No intelligence matches found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'analytics' && (
                    <div className="space-y-8">
                        {/* Summary Header */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 p-6 rounded-2xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <ChartBarSquareIcon className="h-12 w-12 text-blue-400" />
                                </div>
                                <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Global Risk Velocity</p>
                                <div className="flex items-baseline gap-2">
                                    <h3 className={`text-3xl font-black ${analyticsData.velocity > 1 ? 'text-rose-500' : analyticsData.velocity < -1 ? 'text-emerald-500' : 'text-slate-200'}`}>
                                        {analyticsData.velocity > 0 ? '+' : ''}{analyticsData.velocity.toFixed(1)}
                                    </h3>
                                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">7D Delta</span>
                                </div>
                                <div className="mt-2 text-[9px] font-bold text-slate-500 uppercase tracking-tighter">
                                    {analyticsData.velocity > 3 ? 'Critical Threat Acceleration' : analyticsData.velocity > 0 ? 'Elevated Activity Detected' : analyticsData.velocity < -1 ? 'Stabilizing Regional Dynamics' : 'Static Landscape'}
                                </div>
                            </div>
                            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 p-6 rounded-2xl">
                                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">SRM Module Saturation</p>
                                <div className="flex items-baseline gap-2">
                                    <h3 className="text-3xl font-black text-white">{analyticsData.saturation}%</h3>
                                    <span className="text-slate-500 text-xs font-bold">Avg Readiness</span>
                                </div>
                            </div>
                            <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 p-6 rounded-2xl">
                                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Active Intelligence Library</p>
                                <div className="flex items-baseline justify-between gap-2">
                                    <div className="flex items-baseline gap-2">
                                        <h3 className="text-3xl font-black text-white">{reports.length}</h3>
                                        <span className="text-blue-400 text-xs font-bold">Files Categorized</span>
                                    </div>
                                    <button 
                                        onClick={handleManualRefresh}
                                        disabled={isFetching}
                                        className="p-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all border border-blue-500/20"
                                        title="Trigger Manual Intelligence Refresh"
                                    >
                                        <ArrowPathIcon className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                            {/* Main Trend Chart */}
                            <div className="bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-2xl p-8">
                                <div className="flex items-center justify-between mb-8">
                                    <div>
                                        <h4 className="text-lg font-black text-white tracking-tight uppercase">Global Risk Sentiment (6Mo)</h4>
                                        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Aggregated Geopolitical Risk Intensity / Weekly Data Points</p>
                                    </div>
                                    <ChartBarSquareIcon className="h-5 w-5 text-indigo-500" />
                                </div>
                                <div className="h-[300px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={analyticsData.riskTrend}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                            <XAxis 
                                                dataKey="label" 
                                                axisLine={false} 
                                                tickLine={false} 
                                                tick={{ fill: '#64748b', fontSize: 9, fontWeight: 700 }}
                                                dy={10}
                                            />
                                            <YAxis hide domain={[0, 100]} />
                                            <Tooltip 
                                                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                                contentStyle={{ 
                                                    backgroundColor: '#0f172a', 
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    borderRadius: '12px',
                                                    fontSize: '10px',
                                                    fontWeight: '800',
                                                    color: '#fff'
                                                }}
                                            />
                                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                                {analyticsData.riskTrend.map((entry: any, index: number) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color || '#6366f1'} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Module Saturation */}
                            <div className="bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-2xl p-8">
                                <div className="flex items-center justify-between mb-8">
                                    <div>
                                        <h4 className="text-lg font-black text-white tracking-tight uppercase">SRM Module Utilization</h4>
                                        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Completion Stats across 22+ Security Workflows</p>
                                    </div>
                                    <ShieldCheckIcon className="h-5 w-5 text-emerald-500" />
                                </div>
                                <div className="space-y-4">
                                    {analyticsData.moduleCompletion.length > 0 ? analyticsData.moduleCompletion.map((item: any, i: number) => (
                                        <div key={i} className="group cursor-default">
                                            <div className="flex justify-between items-center mb-1.5">
                                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{item.name}</span>
                                                <span className="text-[10px] font-black text-indigo-400">{item.value}%</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-indigo-500 to-blue-600 rounded-full transition-all duration-1000 group-hover:from-indigo-400 group-hover:to-blue-500" 
                                                    style={{ width: `${item.value}%` }} 
                                                />
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center py-12">
                                            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Awaiting Project Saturation Data...</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Suite Participation Matrix */}
                            <div className="bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-2xl p-8">
                                <div className="flex items-center justify-between mb-8">
                                    <div>
                                        <h4 className="text-lg font-black text-white tracking-tight uppercase">Suite Participation Matrix</h4>
                                        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Operational Distribution Across Intelligence Suites</p>
                                    </div>
                                    <ArchiveBoxIcon className="h-5 w-5 text-amber-500" />
                                </div>
                                <div className="h-[250px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={analyticsData.suiteStats}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {analyticsData.suiteStats.map((_: any, index: number) => (
                                                    <Cell key={`cell-${index}`} fill={index === 0 ? '#6366f1' : index === 1 ? '#3b82f6' : '#10b981'} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                contentStyle={{ 
                                                    backgroundColor: '#0f172a', 
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    borderRadius: '8px',
                                                    fontSize: '10px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-4">
                                    {analyticsData.suiteStats.slice(0, 4).map((s: any, i: number) => (
                                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/5">
                                            <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-indigo-500' : i === 1 ? 'bg-blue-500' : 'bg-emerald-500'}`} />
                                            <span className="text-[9px] font-black text-slate-300 uppercase truncate">{s.name} ({s.value})</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Meta-Analysis of Findings Section */}
                            <div className="col-span-1 lg:col-span-2">
                                <div className="bg-slate-900/60 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity">
                                        <SparklesIcon className="h-32 w-32 text-indigo-400" />
                                    </div>
                                    
                                    <div className="relative z-10">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="px-3 py-1 bg-indigo-500/10 text-indigo-400 text-[10px] font-black rounded border border-indigo-500/20 uppercase tracking-wider">
                                                AI Synthesis Active
                                            </div>
                                            <div className="h-4 w-px bg-white/10" />
                                            <h4 className="text-xl font-black text-white uppercase tracking-tighter">Strategic Intelligence Meta-Analysis</h4>
                                        </div>

                                        {isRefreshingMeta ? (
                                            <div className="py-12 flex flex-col items-center justify-center text-center">
                                                <div className="h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
                                                <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest animate-pulse">Running Deep Analysis on Historical Data Library...</p>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                                                <div className="lg:col-span-2">
                                                    <p className="text-slate-200 text-base leading-relaxed font-medium mb-8">
                                                        {analyticsData.findingsMeta.summary}
                                                    </p>
                                                    
                                                    <div className="grid grid-cols-2 gap-6">
                                                        <div className="space-y-4">
                                                            <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Recurring Critical Themes</h5>
                                                            <div className="flex flex-wrap gap-2">
                                                                {analyticsData.findingsMeta.topThemes.map((theme: string, i: number) => (
                                                                    <span key={i} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-bold text-slate-300">
                                                                        {theme}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="space-y-4">
                                                            <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Primary Risk Catalysts</h5>
                                                            <div className="flex flex-wrap gap-2">
                                                                {analyticsData.findingsMeta.threatActors.map((actor: string, i: number) => (
                                                                    <span key={i} className="px-3 py-1.5 bg-rose-500/10 border border-rose-500/20 rounded-lg text-xs font-bold text-rose-400">
                                                                        {actor}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                                                    <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-4">Analytic Confidence</h5>
                                                    <div className="flex items-center gap-4 mb-6">
                                                        <div className="text-4xl font-black text-white">94<span className="text-xs text-slate-400 font-bold ml-1">%</span></div>
                                                        <div className="text-[9px] font-bold text-slate-500 leading-tight uppercase">High Precision Convergence<br/>Based on {reports.length} Data Points</div>
                                                    </div>
                                                    <button className="w-full py-3 bg-white text-slate-900 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-indigo-500 hover:text-white transition-all shadow-xl shadow-black/20">
                                                        Export Strategic Summary
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                             {/* 7-Day Risk Drilling: Recent Critical Events */}
                            <div className="bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-2xl p-8">
                                <div className="flex items-center justify-between mb-6">
                                    <h4 className="text-lg font-black text-white tracking-tight uppercase">7D Risk Alerts (Drilling)</h4>
                                    <FunnelIcon className="h-5 w-5 text-rose-500" />
                                </div>
                                <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                                    {analyticsData.drillEvents?.length > 0 ? analyticsData.drillEvents.map((event: any, i: number) => (
                                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                            <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 animate-pulse`} style={{ backgroundColor: getRiskColor(event.analysis?.risk_score || 50) }} />
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="text-[10px] font-black text-white uppercase tracking-tighter truncate w-32">{event.title}</span>
                                                    <span className="text-[8px] font-bold text-slate-500 uppercase">{event.geo?.country_iso3}</span>
                                                </div>
                                                <p className="text-[9px] text-slate-400 line-clamp-2 leading-relaxed">{event.summary}</p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <span className="px-1.5 py-0.5 bg-white/5 rounded text-[8px] font-black text-slate-500 uppercase tracking-widest">{event.analysis?.category}</span>
                                                    <span className="text-[8px] font-black text-rose-400">SCORE: {event.analysis?.risk_score}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center py-12">
                                            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">No Alerts in Last 7 Days</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Recent Activity Mini-Feed */}
                            <div className="bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-2xl p-8">
                                <h4 className="text-lg font-black text-white tracking-tight uppercase mb-6">Generated Report Insights</h4>
                                <div className="space-y-4">
                                    {reports.slice(0, 5).map((r, i) => (
                                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/10">
                                            <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-indigo-400 flex-shrink-0">
                                                <DocumentChartBarIcon className="h-4 w-4" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-[11px] font-black text-white uppercase tracking-tighter leading-none mb-1">
                                                    {r.reportTitle || r.organizationName || 'Strategic Brief'}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                                                        {formatLocalDate(r.timestamp || r.dateGenerated)}
                                                    </span>
                                                    <div className="w-1 h-1 rounded-full bg-slate-600" />
                                                    <span className={`text-[9px] font-black uppercase tracking-widest ${r.reportType === 'Executive' ? 'text-blue-400' : 'text-slate-400'}`}>
                                                        {r.reportType}
                                                    </span>
                                                </div>
                                            </div>
                                            <button className="p-1.5 bg-white/5 text-slate-400 rounded-lg hover:text-white transition-all">
                                                <EyeIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportHub;
